// smartie-frontend/src/services/analyticsService.ts

// This ensures TypeScript knows about the gtag function on the window object.
declare global {
  interface Window {
    gtag?: (
      command: "event",
      eventName: string,
      eventParams?: {
        [key: string]: string | number | boolean | undefined | null;
      },
    ) => void;
  }
}

export type AnalyticsMode = "fullpage" | "narrow" | "expanded";

type AnalyticsContext = {
  getMode?: () => AnalyticsMode | undefined;
};

const analyticsContext: AnalyticsContext = {};

/**
 * Sets a resolver used to determine the current analytics mode (fullpage | narrow | expanded).
 * The resolver is called on each event so it always reflects the latest UI state.
 */
export function setAnalyticsModeResolver(
  resolver: () => AnalyticsMode | undefined,
): void {
  analyticsContext.getMode = resolver;
}

function getSessionIdFromStorage(): string | undefined {
  try {
    const id = sessionStorage.getItem("smartie-sessionId");
    return id || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Tracks a custom event using Google Analytics 4's gtag.
 * Safely checks if the gtag function is available on the window object.
 * This approach is robust and works even if GA is loaded by another plugin
 * like PixelYourSite, or if it's blocked by an ad blocker.
 *
 * @param {string} eventName The name of the event to track (e.g., 'widget_opened').
 * @param {object} [eventParams] Optional parameters to send with the event.
 */
export const trackEvent = (
  eventName: string,
  eventParams?: { [key: string]: string | number | boolean | undefined | null },
): void => {
  // Build defaults: session_id and mode. Callers can override by supplying these keys.
  const defaultParams: Record<string, string | number | boolean> = {};
  const sessionId = getSessionIdFromStorage();
  if (sessionId && (eventParams?.session_id as unknown) === undefined) {
    defaultParams.session_id = sessionId;
  }
  const mode = analyticsContext.getMode?.();
  if (mode && (eventParams?.mode as unknown) === undefined) {
    defaultParams.mode = mode;
  }

  const mergedParams = {
    ...defaultParams,
    ...(eventParams || {}),
  } as Record<string, string | number | boolean | undefined | null>;

  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", eventName, mergedParams);
    } catch (error) {
      // Use our own logger if we have one, otherwise console.error
      console.error("Error sending GA event:", error);
    }
  }
  // No 'else' block needed to log a warning, as it's common for GA to be blocked.
};
